import { formatMoney } from '@betterment/core';
import { Box, Button, FormikField, H1, H2, RadioGroup, Text } from '@betterment/samba-react';
import { type StepValues } from '@betterment/shared-components';
import { groupBy } from 'es-toolkit';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';

import { LegalAccountTaxationType, ManagedSubAccount } from '../../graphql/schema';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { urlBuilder } from '../../Shared/urlBuilder';
import {
  TAX_COORDINATED_PORTFOLIO_FLOW_CONFIG,
  useTaxCoordinatedPortfolioFlowContext,
  useTaxCoordinatedPortfolioFlowStep,
} from '../FlowConfig';

type FormValues = StepValues<typeof TAX_COORDINATED_PORTFOLIO_FLOW_CONFIG, 'accounts_selection'>;

const TAXATION_TYPE_LABEL_MAP: Record<LegalAccountTaxationType, string> = {
  [LegalAccountTaxationType.Holding_529]: 'HOLDING 529',
  [LegalAccountTaxationType.Hsa]: 'HEALTH SAVINGS ACCOUNT',
  [LegalAccountTaxationType.Roth_401K]: 'ROTH 401(K)',
  [LegalAccountTaxationType.RothIra]: 'ROTH IRA',
  [LegalAccountTaxationType.SepIra]: 'SEP IRA',
  [LegalAccountTaxationType.Taxable]: 'INDIVIDUAL TAXABLE',
  [LegalAccountTaxationType.Traditional_401K]: 'TRADITIONAL 401(K)',
  [LegalAccountTaxationType.TraditionalIra]: 'TRADITIONAL IRA',
};

const TaxationTypeSection = ({
  heading,
  taxationTypeOrder,
  groupedSubAccounts,
}: {
  heading: string;
  taxationTypeOrder: LegalAccountTaxationType[];
  groupedSubAccounts: Record<
    LegalAccountTaxationType,
    (ManagedSubAccount & { isTaxCoordinated?: boolean; portfolioConfigurationDescription?: string })[]
  >;
}) => (
  <Box>
    <H2
      variant="title200"
      css={{
        marginBottom: 'space.200',
      }}
    >
      {heading}
    </H2>
    <Box css={{ display: 'flex', flexDirection: 'column', rowGap: 'space.200' }}>
      {taxationTypeOrder.map(taxationType => (
        <FormikField key={taxationType} name={taxationType}>
          <Box css={{ border: '1px solid', borderColor: 'border.weak', borderRadius: 'medium' }}>
            <Text
              as="p"
              variant="smallCaps"
              css={{
                backgroundColor: 'background.neutral',
                paddingX: 'space.200',
                paddingY: 'space.100',
                color: 'text.weak',
              }}
            >
              {TAXATION_TYPE_LABEL_MAP[taxationType]}
            </Text>
            <FormikField.Control>
              <RadioGroup
                disabled={groupedSubAccounts[taxationType].some(item => item.isTaxCoordinated)}
                css={{ padding: 'space.200', display: 'flex', flexDirection: 'column', rowGap: 'space.200' }}
              >
                {groupedSubAccounts[taxationType].map(subAccount => (
                  <RadioGroup.ItemContainer
                    key={subAccount.id}
                    css={{
                      display: 'flex',
                      flexDirection: 'row',
                      columnGap: 'space.200',
                      alignItems: {
                        base: 'flex-start',
                        md: 'center',
                      },
                    }}
                  >
                    <RadioGroup.Item value={subAccount.id} />
                    <RadioGroup.ItemLabel
                      css={{
                        width: '100%',
                        display: 'flex',
                        rowGap: 'space.150',
                        flexDirection: {
                          base: 'column',
                          md: 'row',
                        },
                        alignItems: {
                          base: 'flex-start',
                          md: 'center',
                        },
                        justifyContent: {
                          base: 'flex-start',
                          md: 'space-between',
                        },
                      }}
                    >
                      <Box
                        css={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Text>{subAccount.name}</Text>
                        <Text
                          variant="text50"
                          css={{
                            color: 'text.weak',
                          }}
                        >
                          {subAccount.portfolioConfigurationDescription}
                        </Text>
                      </Box>
                      <Text>{formatMoney(subAccount.balance)}</Text>
                    </RadioGroup.ItemLabel>
                  </RadioGroup.ItemContainer>
                ))}
              </RadioGroup>
            </FormikField.Control>
          </Box>
        </FormikField>
      ))}
    </Box>
  </Box>
);

export const AccountsSelection = () => {
  const navigate = useNavigate();
  const { flowId } = useParams<{ flowId: string }>();
  const { commit } = useTaxCoordinatedPortfolioFlowStep('accounts_selection');
  const { taxCoordinationAccountOptions } = useTaxCoordinatedPortfolioFlowContext();

  const subAccounts = taxCoordinationAccountOptions.flatMap(taxCoordinationAccount =>
    (taxCoordinationAccount.envelopeAccount.subAccounts || []).map(subAccount => ({
      ...subAccount,
      isTaxCoordinated: taxCoordinationAccount.envelopeAccount.isTaxCoordinated,
      portfolioConfigurationDescription: taxCoordinationAccount.envelopeAccount.portfolio?.configurationDescription,
    }))
  );
  const subAccountsGroupedByTaxationType = groupBy(subAccounts, item => item.legalAccount.taxationType);
  const hasTaxCoordinatedTaxableSubAccount = subAccountsGroupedByTaxationType[LegalAccountTaxationType.Taxable].some(
    item => item.isTaxCoordinated
  );
  const taxAdvantagedTaxationTypeOrder = Object.keys(subAccountsGroupedByTaxationType)
    .filter(taxationType => taxationType !== LegalAccountTaxationType.Taxable)
    .sort((_, taxationType) =>
      (subAccountsGroupedByTaxationType[taxationType as LegalAccountTaxationType] ?? []).some(
        item => item.isTaxCoordinated
      )
        ? 1
        : -1
    ) as LegalAccountTaxationType[];

  const initialValues = Object.fromEntries(
    Object.entries(subAccountsGroupedByTaxationType).map(([key, accounts]) => [
      key,
      accounts.find(a => a.isTaxCoordinated)?.id ?? undefined,
    ])
  ) as FormValues;

  const validate = (values: FormValues) => {
    const selected = Object.values(values).filter(Boolean);
    if (selected.length < 2) return { selection: 'Select at least two accounts.' };
    const hasNewSelection = Object.entries(values).some(
      ([key, value]) => !!value && value !== initialValues[key as LegalAccountTaxationType]
    );
    if (!hasNewSelection) return { selection: 'Select at least one new account.' };
    return {};
  };

  const handleSubmit = (values: FormValues) => {
    commit(values);
    void navigate(`${urlBuilder.appTcp.path}/${flowId}/portfolio_selection`);
  };

  return (
    <WorkflowLayout
      workflowTitle={null}
      onBack={() => navigate(-1)}
      onClose={() => navigate('/')}
      pageTitle="Tax Coordinated Portfolio"
    >
      <H1 variant="pageTitle" css={{ marginBottom: 'space.200', textWrapStyle: 'auto' }}>
        What accounts do you want to tax-coordinate?
      </H1>
      <Text as="p" variant="text100" css={{ marginBottom: 'space.400' }}>
        Select at least two accounts to create your Tax-Coordinated Portfolio.
      </Text>
      <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
        {({ errors: _errors }) => {
          const errors = _errors as { selection?: string };
          return (
            <Form>
              <Box css={{ display: 'flex', flexDirection: 'column', rowGap: 'space.400' }}>
                {hasTaxCoordinatedTaxableSubAccount && (
                  <TaxationTypeSection
                    heading="Taxable"
                    taxationTypeOrder={[LegalAccountTaxationType.Taxable]}
                    groupedSubAccounts={subAccountsGroupedByTaxationType}
                  />
                )}
                <TaxationTypeSection
                  heading="Tax-advantaged"
                  taxationTypeOrder={taxAdvantagedTaxationTypeOrder}
                  groupedSubAccounts={subAccountsGroupedByTaxationType}
                />
                {!hasTaxCoordinatedTaxableSubAccount && (
                  <TaxationTypeSection
                    heading="Taxable"
                    taxationTypeOrder={[LegalAccountTaxationType.Taxable]}
                    groupedSubAccounts={subAccountsGroupedByTaxationType}
                  />
                )}
                {errors.selection !== undefined && (
                  <Text variant="text100" css={{ color: 'text.critical' }}>
                    {errors.selection}
                  </Text>
                )}
                <Button
                  type="submit"
                  css={{
                    width: {
                      base: '100%',
                      md: 'fit-content',
                    },
                  }}
                >
                  Continue
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </WorkflowLayout>
  );
};
